// ============================================================================
// Ops bucket — the one thing a hire needs next
// ============================================================================
// Puts every hire in EXACTLY ONE bucket, so the Creator Payments tabs are a
// partition: a hire appears once, and the tab counts sum to the total.
//
// Why this exists: the tabs used to filter on three unrelated axes at once —
// delivery lifecycle (In Progress / Partial Completed / Completed), the creator's
// money (Paid) and the brand's money (Refundable / Refunded). Those axes were
// accidentally aligned while a hire could only ever be paid in full on a complete
// delivery and refunded only when it failed. Partial refunds and partial payouts
// broke the alignment: a hire can now be legitimately mid-delivery AND partly
// refunded AND partly paid, which put one row in three tabs and made the counts
// impossible to reconcile (128 + 15 + 5 + 4 + 43 + 9 against a total of 150).
//
// The fix is not to hide the overlap — it's real, and the row's badges should keep
// showing all of it. The fix is to stop the TABS from claiming to partition on
// something that doesn't partition, and have them answer one question instead:
// what does ops need to do with this hire next?
//
// Precedence is by urgency, and the first match wins. Money owed outranks a
// decision to make, which outranks waiting.
// ============================================================================

import { extensionRequestPending, refundState, type RefundableHire } from './refunds'
import { liveTrolleyPayout, payoutClosed, payoutState, stillDelivering, type PayableHire } from './payouts'

export type OpsBucket = 'needs_refund' | 'needs_payout' | 'needs_review' | 'delivering' | 'settled'

/** Everything both money rules need, plus what only this module reads. */
export interface QueueableHire extends RefundableHire, PayableHire {
  /** Amount actually paid out. The money lens reads "partial" off this rather
   *  than off a flag: a payout recorded below the hire's rate IS the partial one. */
  payment_amount_cents?: number | null
  deadline_extension_requested_at?: string | null
  deadline_extended_at?: string | null
  deadline_extension_rejected_at?: string | null
}

/**
 * An extension the creator asked for and nobody has granted or rejected.
 *
 * Re-exported rather than redefined. This predicate decides three separate things
 * — whether the refund button appears, whether the auto-refund cron skips a hire,
 * and which bucket the row lands in — and it was written out by hand in each
 * place. The refund button and the cron then disagreed for weeks: the cron
 * skipped hires with an open request, the button refunded them anyway.
 */
export { extensionRequestPending as extensionPending } from './refunds'

function requiredVideos(hire: QueueableHire): number {
  return hire.videos_required ?? hire.package_size ?? 0
}

/**
 * Needs a human, but not a payment. Kept separate from `settled` so a stuck row
 * can't hide in it, and separate from `delivering` so it doesn't read as "wait".
 */
function needsReview(hire: QueueableHire, deliveredVideos: number, pendingVideos = 0): boolean {
  // Submitted videos still awaiting an admin decision. Nothing can pay until
  // every one is approved, so the hire's next action is a review, not a payout.
  //
  // Only while a payout is still possible, though. This clause is a MONEY gate,
  // not a bookkeeping one — it exists so a payout can't land on unreviewed work.
  // Where no payout can ever land, a review changes nothing, and HireDetail
  // doesn't even render the Approve/Reject buttons (same payoutClosed rule), so
  // the tab was demanding an action that has no button.
  //
  // This is what filled Needs review the day manual review shipped: every hire
  // settled BEFORE it — refunded to the brand or paid to the creator — carries
  // videos that were auto-approved back when review_status null counted as
  // approved. They were never reviewed because review didn't exist, and they
  // cannot be reviewed now because the money is closed. They belong in Settled.
  //
  // And not while the creator is still delivering. A hire 1 video into a package
  // of 5, three weeks from its deadline, is Delivering — the videos in so far
  // aren't the final set, and no payout can be recorded until either the package
  // lands or the window closes and a refund settles the gap. Neither is possible
  // yet, so there is nothing for a review to unblock.
  //
  // A COMPLETE delivery is the opposite and stays here: the creator is finished
  // and the review is the last thing between them and their money. That is the
  // line — short with time left is Delivering, everything delivered is a review.
  if (pendingVideos > 0
    && !payoutClosed(hire, deliveredVideos)
    && !stillDelivering(hire, deliveredVideos)) return true

  // Ops has to grant or reject it; nothing else moves until they do. Unless the
  // money is closed (creator paid, or brand refunded in full on a short
  // delivery — see payoutClosed): an answer moves nothing, and the row belongs
  // in Settled rather than asking for a decision forever.
  if (extensionRequestPending(hire) && !payoutClosed(hire, deliveredVideos)) return true

  // Every video is in, but the hire was never flipped back to 'completed', so no
  // payout can be recorded against it. Normally a rejected-then-replaced video
  // whose revert didn't land. Without this bucket the row falls to 'settled' —
  // filed as done while the creator is still owed the whole package.
  const required = requiredVideos(hire)
  const complete = required > 0 && deliveredVideos >= required
  if (complete && hire.status !== 'completed' && (hire.payment_status ?? 'unpaid') === 'unpaid') return true

  return false
}

/**
 * @param deliveredVideos videos counting toward the requirement — submitted, not
 *        admin-rejected, not deleted. The same count refundState() and
 *        payoutState() take, since this is built from both.
 * @param pendingVideos the slice of those still awaiting an admin decision.
 */
export function opsBucket(hire: QueueableHire, deliveredVideos: number, pendingVideos = 0): OpsBucket {
  // A brand waiting on money it is owed comes first — it's the only party here
  // that has paid for something it didn't get.
  if (refundState(hire, deliveredVideos).eligible) return 'needs_refund'

  // Then the creator: work delivered, payout not recorded. (A partial payout only
  // becomes eligible once the brand's refund has landed + no video is awaiting
  // review, so these two can't fight over the same hire — see payouts.ts.)
  if (payoutState(hire, deliveredVideos, pendingVideos).eligible) return 'needs_payout'

  // A Trolley payout that is out but not yet confirmed stays with the payout
  // work, not 'settled': the hire isn't paid until Trolley says so, and one that
  // lost contact mid-send needs ops to Sync it.
  if ((hire.payment_status ?? 'unpaid') === 'unpaid' && liveTrolleyPayout(hire)) return 'needs_payout'

  if (needsReview(hire, deliveredVideos, pendingVideos)) return 'needs_review'

  // Nothing to do yet: delivery is short but the creator still has time. Same
  // test payoutState uses to explain the Pay button, so the tab and the tooltip
  // always agree about whether the clock has run out.
  if (stillDelivering(hire, deliveredVideos)) return 'delivering'

  // Nothing owed either way and no decision outstanding.
  return 'settled'
}

// ── Money lens ──────────────────────────────────────────────────────────────
// The axis the tabs no longer carry. It's a filter rather than a tab precisely
// because it OVERLAPS the buckets: a partly-refunded hire can be awaiting a
// payout, or settled, or still delivering, and all three are correct.
// ============================================================================

export type MoneyLens =
  | 'any'
  | 'creator_unpaid'
  | 'creator_partial'
  | 'creator_paid'
  | 'brand_refunded'
  | 'brand_partial_refund'
  | 'brand_full_refund'

export function matchesMoneyLens(hire: QueueableHire, lens: MoneyLens): boolean {
  const paid = (hire.payment_status ?? 'unpaid') === 'paid'
  const paidCents = hire.payment_amount_cents ?? 0
  const rate = hire.creator_rate_cents ?? 0
  const collected = (hire.credit_applied_cents ?? 0) + (hire.card_charged_cents ?? 0)
  const refunded = hire.refunded_credit_cents ?? 0

  switch (lens) {
    case 'any':                  return true
    case 'creator_unpaid':       return !paid
    // "Partial" is read off the amount, not a flag: a payout recorded for less
    // than the hire's rate IS the partial one.
    case 'creator_partial':      return paid && paidCents > 0 && rate > 0 && paidCents < rate
    case 'creator_paid':         return paid && (rate <= 0 || paidCents >= rate)
    case 'brand_refunded':       return refunded > 0
    case 'brand_partial_refund': return refunded > 0 && !(collected > 0 && refunded >= collected)
    case 'brand_full_refund':    return collected > 0 && refunded >= collected
  }
}
